import React, { useState, useEffect, useMemo } from 'react';
import { researchReportsBulkAPI } from '../services/api';
import ModalShell from './ModalShell';

const BulkReportDownloadModal = ({
  isOpen,
  onClose,
  isGenerating,
  jobProgress,
  jobStatus,
  message,
  onGenerate,
}) => {
  const [selected, setSelected] = useState(new Set());
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Portfolio selector state
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setSelectedPortfolio('');
    setError(null);
    fetchFunds();
    fetchPortfolios();
  }, [isOpen]);

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const data = await researchReportsBulkAPI.getFunds();
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error fetching funds for bulk download:', err);
      setError('Failed to load funds');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const data = await researchReportsBulkAPI.getPortfolios();
      setPortfolios(data.portfolios || []);
    } catch (err) {
      console.error('Error fetching portfolios for bulk download:', err);
    }
  };

  // Group portfolios by suite for optgroup display
  const suites = useMemo(() => {
    const seen = [];
    portfolios.forEach(p => {
      if (!seen.includes(p.suite)) seen.push(p.suite);
    });
    return seen;
  }, [portfolios]);

  const handlePortfolioSelect = (e) => {
    const value = e.target.value;
    setSelectedPortfolio(value);
    if (!value) return;
    const portfolio = portfolios.find(p => p.code === value);
    if (portfolio) {
      setSelected(new Set(portfolio.matching_apirs));
    }
  };

  const toggleItem = (apir) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(apir)) next.delete(apir);
      else next.add(apir);
      return next;
    });
  };

  const selectGroup = (group, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      group.funds.forEach(f => {
        if (checked) next.add(f.apir);
        else next.delete(f.apir);
      });
      return next;
    });
  };

  const isGroupAllSelected = (group) =>
    group.funds.length > 0 &&
    group.funds.every(f => selected.has(f.apir));

  const isGroupPartial = (group) => {
    const count = group.funds.filter(f => selected.has(f.apir)).length;
    return count > 0 && count < group.funds.length;
  };

  const selectAll = () => {
    const all = new Set();
    groups.forEach(g => g.funds.forEach(f => all.add(f.apir)));
    setSelected(all);
  };

  const selectNone = () => setSelected(new Set());

  const handleGenerate = () => {
    onGenerate(Array.from(selected));
  };

  const totalFunds = groups.reduce((sum, g) => sum + g.funds.length, 0);

  const guardedClose = isGenerating ? undefined : onClose;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={guardedClose}
      title="Bulk Download Research Reports"
      maxWidth="700px"
      initialWidth="700px"
      initialHeight="80vh"
    >
      {/* Body */}
      <div style={{ padding: '16px 20px', flex: 1, overflowY: 'auto' }}>
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <p className="mt-2 text-muted mb-0" style={{ fontSize: '0.875rem' }}>Loading funds...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2" style={{ fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle me-1"></i>{error}
          </div>
        )}

        {!loading && !error && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Note: 10 reports will take around 3 minutes to generate.
            </p>

            {/* Portfolio selector */}
            {portfolios.length > 0 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#072040', marginBottom: '0.25rem', display: 'block' }}>
                  Select by Portfolio
                </label>
                <select
                  className="form-select form-select-sm"
                  value={selectedPortfolio}
                  onChange={handlePortfolioSelect}
                  disabled={isGenerating}
                  style={{ fontSize: '0.85rem' }}
                >
                  <option value="">-- Choose a portfolio --</option>
                  {suites.map(suite => (
                    <optgroup key={suite} label={suite}>
                      {portfolios.filter(p => p.suite === suite).map(p => (
                        <option key={p.code} value={p.code}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            <div className="bulk-cascade-bar">
              <button className="btn btn-sm btn-outline-primary" onClick={selectAll} disabled={isGenerating}>
                Select All
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={selectNone} disabled={isGenerating}>
                Clear
              </button>
            </div>

            {groups.map((group) => (
              <div key={group.asset_class} style={{ marginBottom: '0.75rem' }}>
                <label className="bulk-group-header">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isGroupAllSelected(group)}
                    ref={input => {
                      if (input) input.indeterminate = isGroupPartial(group);
                    }}
                    onChange={(e) => selectGroup(group, e.target.checked)}
                    disabled={isGenerating}
                  />
                  {group.asset_class} ({group.funds.length})
                </label>
                {group.funds.map(f => (
                  <label key={f.apir} className="bulk-portfolio-item">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selected.has(f.apir)}
                      onChange={() => toggleItem(f.apir)}
                      disabled={isGenerating}
                    />
                    {f.name}
                  </label>
                ))}
              </div>
            ))}
          </>
        )}

        {isGenerating && (
          <div style={{ marginTop: '1rem' }}>
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">
                {jobStatus === 'pending' && 'Initialising...'}
                {jobStatus === 'running' && 'Generating research reports...'}
                {jobStatus === 'completed' && 'Organising reports...'}
              </small>
              <small className="text-muted">{jobProgress}%</small>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${jobProgress}%`, background: '#072040' }}
                aria-valuenow={jobProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>
        )}

        {message && (
          <div
            className={`alert alert-${message.type === 'error' ? 'danger' : message.type === 'warning' ? 'warning' : message.type === 'info' ? 'info' : 'success'} py-2 mt-3`}
            style={{ fontSize: '0.85rem' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
          {selected.size} of {totalFunds} selected
        </span>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isGenerating || selected.size === 0}
            style={{ background: '#072040', borderColor: '#072040' }}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Generating... {jobProgress}%
              </>
            ) : (
              <>
                <i className="bi bi-download me-2"></i>
                Download ({selected.size})
              </>
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default BulkReportDownloadModal;
